import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
  AlertCircle,
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface LoginForm {
  email: string;
  password: string;
}

/* ───────────────────────────────────────────────────────────
   Paleta "Cocina industrial editorial"
   Bone cálido + ink + acento terracota tostado + acero.
   Inspirada en imprenta, papel craft, cobre y acero inox.
   Aislada a este archivo — no toca tokens globales.
   ─────────────────────────────────────────────────────────── */
const FONT_DISPLAY =
  "'Instrument Serif', 'Fraunces', Georgia, 'Times New Roman', serif";

const APP_VERSION = '1.0';

const detectarEntorno = (): 'QA' | 'PROD' | 'DEV' => {
  if (typeof window === 'undefined') return 'DEV';
  const h = window.location.hostname.toLowerCase();
  if (
    h.includes('qa.suminuta') ||
    h.includes('quicktaste-qa') ||
    h.includes('-qa.')
  )
    return 'QA';
  if (h === 'localhost' || h.startsWith('127.') || h.startsWith('192.168.'))
    return 'DEV';
  return 'PROD';
};

const formatearBuild = (raw: string): string => {
  if (!raw || raw === 'dev') return 'Build de desarrollo';
  try {
    const iso = raw.replace(' ', 'T') + 'Z';
    const fecha = new Date(iso);
    if (isNaN(fecha.getTime())) return `Build ${raw}`;
    const fmtFecha = new Intl.DateTimeFormat('es-CL', {
      timeZone: 'America/Santiago',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const fmtHora = new Intl.DateTimeFormat('es-CL', {
      timeZone: 'America/Santiago',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `Build ${fmtFecha.format(fecha)} · ${fmtHora.format(fecha)} hora Chile`;
  } catch {
    return `Build ${raw}`;
  }
};

const Login: React.FC = () => {
  const [formData, setFormData] = useState<LoginForm>({
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const { login, isAuthenticated, clearError } = useAuth();

  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setAuthLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(formData);
      navigate('/dashboard');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Error al iniciar sesión';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f1ea]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#c4654a] border-t-transparent"></div>
      </div>
    );
  }

  const year = new Date().getFullYear();
  const buildLine = formatearBuild(__BUILD_TIME__);
  const env = detectarEntorno();

  // Chips de entorno — paleta cálida, no de tecnologia agresiva.
  const envStyles: Record<typeof env, string> = {
    QA: 'border-[#b5832f]/40 bg-[#f0e5cc] text-[#6b4e1e]',
    PROD: 'border-[#4a6b4d]/40 bg-[#e3ebe1] text-[#2d4a3e]',
    DEV: 'border-[#6b6760]/40 bg-[#e8e3d8] text-[#4a463e]',
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f5f1ea] text-[#1d1b18] font-sans antialiased">
      {/* ===== Decoración de fondo — capas cálidas ===== */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        {/* Vignette cálida sutil */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 100% 80% at 50% 50%, transparent 50%, rgba(85,70,55,0.12) 100%)',
          }}
        />
        {/* Glow direccional terracota muy sutil — calidez de horno */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 70% 50% at 15% 100%, rgba(196,101,74,0.10) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 100% 0%, rgba(168,117,68,0.08) 0%, transparent 60%)',
          }}
        />
        {/* Grano de papel craft (grain visible pero sutil) */}
        <div
          className="absolute inset-0 opacity-[0.18] mix-blend-multiply"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.45  0 0 0 0 0.38  0 0 0 0 0.30  0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
          }}
        />
        {/* Grid tipo plano técnico — líneas finísimas */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(to right, #1d1b18 1px, transparent 1px), linear-gradient(to bottom, #1d1b18 1px, transparent 1px)',
            backgroundSize: '72px 72px',
          }}
        />
      </div>

      {/* ===== Top bar ===== */}
      <header
        className="relative z-10 flex items-center justify-between px-6 sm:px-10 lg:px-14 pt-6"
        style={{ animation: 'fadeIn 0.6s 0.05s ease-out both' }}
      >
        <div className="flex items-center gap-2 text-[11px] text-[#6b6760] tracking-wide">
          <span className="relative inline-flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#4a6b4d] opacity-50 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#4a6b4d]" />
          </span>
          <span>Sistemas operativos</span>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`px-2 py-0.5 rounded-md text-[10px] font-medium tracking-[0.14em] border ${envStyles[env]}`}
            title={`Entorno: ${env}`}
          >
            {env}
          </span>
          <span className="text-[11px] text-[#9b958b] font-mono">
            v{APP_VERSION}
          </span>
        </div>
      </header>

      {/* ===== Centro ===== */}
      <main className="relative z-10 flex flex-col lg:flex-row items-center lg:items-stretch justify-center gap-12 lg:gap-20 px-6 sm:px-10 lg:px-14 py-10 lg:py-16 min-h-[calc(100vh-120px)]">
        {/* Editorial denso */}
        <section className="flex-1 max-w-lg lg:max-w-none lg:flex-[0_1_440px] flex flex-col justify-center gap-8">
          <div
            className="flex items-center gap-3"
            style={{ animation: 'fadeUp 0.7s 0.1s ease-out both' }}
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-md border border-[#c4654a]/35 bg-[#c4654a]/8">
              <span
                className="text-[#c4654a] text-base leading-none"
                style={{ fontFamily: FONT_DISPLAY }}
              >
                S
              </span>
            </div>
            <span className="text-[11px] tracking-[0.22em] uppercase text-[#6b6760]">
              Suminuta · Plataforma de gestión
            </span>
          </div>

          <h1
            className="text-[68px] sm:text-[88px] lg:text-[124px] leading-[0.92] tracking-[-0.02em] text-[#1d1b18]"
            style={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 400,
              animation: 'fadeUp 0.7s 0.2s ease-out both',
            }}
          >
            Suminuta
          </h1>

          <div
            className="space-y-4"
            style={{ animation: 'fadeUp 0.7s 0.3s ease-out both' }}
          >
            <p
              className="text-xl lg:text-2xl text-[#8b4a32] italic leading-snug"
              style={{ fontFamily: FONT_DISPLAY }}
            >
              Faena tras faena.
            </p>
            <p className="text-sm leading-relaxed text-[#6b6760] max-w-md">
              Gestión de minutas, pedidos, producción de cocina e inventario
              para servicios de alimentación industrial.
            </p>
          </div>

          {/* Lista de capacidades — bullets cuadrados estilo plano técnico */}
          <div
            className="hidden lg:flex flex-col gap-3 pt-4"
            style={{ animation: 'fadeUp 0.7s 0.4s ease-out both' }}
          >
            {[
              ['Planificación', 'Minutas con fondo y guarnición'],
              ['Operación', 'Pedidos, cocina y stock en vivo'],
              ['Trazabilidad', 'Lotes, mermas y costos por receta'],
            ].map(([label, desc]) => (
              <div key={label} className="flex items-start gap-3">
                <div className="mt-1.5 h-[6px] w-[6px] bg-[#c4654a]" />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#1d1b18]">
                    {label}
                  </div>
                  <div className="text-xs text-[#9b958b] mt-0.5">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Card del form */}
        <section
          className="w-full max-w-md lg:w-[420px] flex-shrink-0"
          style={{ animation: 'fadeUp 0.7s 0.25s ease-out both' }}
        >
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{
              background:
                'linear-gradient(180deg, #fefcf7 0%, #f8f4ec 100%)',
              border: '1px solid rgba(29,27,24,0.08)',
              boxShadow:
                '0 1px 0 rgba(255,255,255,0.8) inset, 0 1px 2px rgba(29,27,24,0.04), 0 12px 32px -8px rgba(29,27,24,0.12), 0 24px 48px -16px rgba(29,27,24,0.10)',
            }}
          >
            {/* Línea cobre superior, hairline */}
            <div
              className="absolute top-0 left-8 right-8 h-px"
              style={{
                background:
                  'linear-gradient(to right, transparent, rgba(196,101,74,0.5), transparent)',
              }}
            />

            <div className="p-8 lg:p-10">
              {/* Header del card */}
              <div className="flex items-center justify-between mb-7">
                <div className="flex items-center gap-2">
                  <ShieldCheck
                    className="h-3.5 w-3.5 text-[#c4654a]"
                    aria-hidden="true"
                  />
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#6b6760]">
                    Acceso seguro
                  </span>
                </div>
                <div className="h-px w-12 bg-gradient-to-r from-[#c4654a]/40 to-transparent" />
              </div>

              <h2
                className="text-[36px] leading-tight text-[#1d1b18] mb-2"
                style={{ fontFamily: FONT_DISPLAY, fontWeight: 400 }}
              >
                Inicia sesión.
              </h2>
              <p className="text-sm text-[#6b6760] mb-8">
                Ingresa tus credenciales para continuar.
              </p>

              {error && (
                <div
                  role="alert"
                  aria-live="polite"
                  className="mb-6 px-3.5 py-2.5 rounded-lg border border-[#a8403a]/30 bg-[#a8403a]/[0.06] flex items-start gap-2.5"
                >
                  <AlertCircle
                    className="h-4 w-4 text-[#a8403a] mt-0.5 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-[13px] leading-relaxed text-[#7a302b]">
                    {error}
                  </span>
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                className="space-y-5"
                noValidate
              >
                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-[10px] font-medium tracking-[0.2em] text-[#6b6760] uppercase mb-2"
                  >
                    Correo electrónico
                  </label>
                  <div className="relative group">
                    <Mail
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9b958b] group-focus-within:text-[#c4654a] transition-colors"
                      aria-hidden="true"
                    />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="usuario@suminuta.com"
                      aria-required="true"
                      className="w-full pl-9 pr-3 py-2.5 bg-[#fdfbf5] text-[#1d1b18] text-sm placeholder:text-[#b8b0a3] rounded-lg border border-[#dad3c6] focus:border-[#c4654a]/60 focus:outline-none focus:ring-2 focus:ring-[#c4654a]/15 transition-all"
                    />
                  </div>
                </div>

                {/* Contraseña */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-[10px] font-medium tracking-[0.2em] text-[#6b6760] uppercase mb-2"
                  >
                    Contraseña
                  </label>
                  <div className="relative group">
                    <Lock
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9b958b] group-focus-within:text-[#c4654a] transition-colors"
                      aria-hidden="true"
                    />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="••••••••"
                      aria-required="true"
                      className="w-full pl-9 pr-10 py-2.5 bg-[#fdfbf5] text-[#1d1b18] text-sm placeholder:text-[#b8b0a3] rounded-lg border border-[#dad3c6] focus:border-[#c4654a]/60 focus:outline-none focus:ring-2 focus:ring-[#c4654a]/15 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={
                        showPassword
                          ? 'Ocultar contraseña'
                          : 'Mostrar contraseña'
                      }
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-[#9b958b] hover:text-[#1d1b18] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c4654a]/30 transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Botón submit — charcoal sólido sobre cream, contraste editorial */}
                <button
                  type="submit"
                  disabled={isLoading}
                  aria-disabled={isLoading}
                  className="group relative w-full mt-2 py-3 px-5 rounded-lg overflow-hidden text-[#f5f1ea] text-[13px] font-medium tracking-wide transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c4654a]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#fefcf7] disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{
                    background:
                      'linear-gradient(180deg, #2a2620 0%, #1d1b18 100%)',
                    border: '1px solid #1d1b18',
                    boxShadow:
                      '0 1px 0 rgba(255,255,255,0.08) inset, 0 1px 2px rgba(29,27,24,0.2), 0 8px 16px -8px rgba(29,27,24,0.4)',
                  }}
                >
                  {/* Hairline cobre inferior — micro detalle */}
                  <span
                    className="pointer-events-none absolute inset-x-6 bottom-0 h-px opacity-50"
                    style={{
                      background:
                        'linear-gradient(to right, transparent, #c4654a, transparent)',
                    }}
                  />
                  {/* Shimmer cobre durante loading */}
                  {isLoading && (
                    <span
                      className="pointer-events-none absolute inset-x-0 top-0 h-[2px]"
                      style={{
                        background:
                          'linear-gradient(to right, transparent, #c4654a, transparent)',
                        animation: 'shimmer 1.2s linear infinite',
                      }}
                    />
                  )}

                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2.5">
                      <span className="h-3.5 w-3.5 border-2 border-[#c4654a] border-t-transparent rounded-full animate-spin" />
                      <span>Iniciando sesión…</span>
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <span>Iniciar sesión</span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  )}
                </button>
              </form>

              {/* Divisor "Soporte" */}
              <div className="mt-8 flex items-center gap-3">
                <div className="flex-1 h-px bg-[#dad3c6]" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#9b958b]">
                  Soporte
                </span>
                <div className="flex-1 h-px bg-[#dad3c6]" />
              </div>

              <p className="mt-5 text-[13px] text-[#6b6760] text-center">
                ¿Problemas para acceder?{' '}
                <a
                  href="mailto:soporte@suminuta.com"
                  className="inline-flex items-center gap-1 text-[#1d1b18] hover:text-[#c4654a] focus:outline-none focus-visible:text-[#c4654a] underline underline-offset-4 decoration-[#b8b0a3] hover:decoration-[#c4654a] transition-colors"
                >
                  Contacta al administrador
                  <ArrowUpRight className="h-3 w-3" />
                </a>
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* ===== Footer ===== */}
      <footer
        className="relative z-10 px-6 sm:px-10 lg:px-14 pb-5 pt-2"
        style={{ animation: 'fadeIn 0.6s 0.5s ease-out both' }}
      >
        <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-[#9b958b]">
          <span>
            © {year} · Suminuta <span className="text-[#b8b0a3]">by</span>{' '}
            NexGen Software
          </span>
          <span className="font-mono opacity-90" title={__APP_COMMIT__}>
            {buildLine}
          </span>
        </div>
      </footer>

      {/* ===== Keyframes locales ===== */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

export default Login;
